import { StaleRecipeWriteError } from '../../../../services/recipeWriteConflict';
import type { SourceRestaurantData } from "../../../../stores/appSourceRestaurantDataStore";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { TabbedSections } from "../../../ui/TabbedSections";
import { primaryText, secondaryText, greyTextColor, borderPrimary, placeholderPrimary, greenColorClass } from "../../../../styling/stylingHelper";
import { useOrderHistory, Unit } from "../../../../stores/appOrderHistoryStore";
import { useCostPriceStore } from "../../../../stores/appCostPriceStore";
import { useSubrecipesStore } from "../../../../stores/appSubrecipesStore";
import { useAppUserDefaultStore } from "../../../../stores/appUserDefaultStore";
import { ReviewIngredient, ReviewAlternative } from "../../../../types/reviewTypes";
import { useProductSearch } from "../../../../hooks/useProductSearch";
import { useDebounce } from "../../../../hooks/useDebounce";
import { SupplierLogo } from "../../../ui/SupplierLogo";
import { calculateSubrecipeUnitCost } from "../../../../utils/costPriceUtils";
import { eur } from "../../../orderhistory/utils";
import { calculateUnitPrice, getProductAggregationKey } from "../../../orderhistory/aggregations";
import { getCategoryDisplayName } from "../../../orderhistory/categoryUtils";
import { Platform } from "../../../../types/Platforms";
import { isKnownPlatform, templateQuantityInUnit } from "./reviewUtils";
import { MatchingIngredientRef, SwapReviewAdapter, SwapCandidate } from "./reviewAdapter";
import { useProductUsage } from "./productUsage";
import UsageBadge from "./UsageBadge";
import ApplyToOtherItemsPanel from "./ApplyToOtherItemsPanel";

interface SwapIngredientPanelProps {
    requireQuantityOnUnitChange?: boolean;
    productMatching?: boolean;
    ingredient: ReviewIngredient;
    ingredientIndex: number;
    /** Cost price id or subrecipe id, depending on the flow the adapter serves. */
    itemId: string;
    dataOverride?: SourceRestaurantData;
    defaultWastePercentage: number;
    adapter: SwapReviewAdapter;
    onDone: () => void;
    /**
     * Optional per-candidate annotation, rendered by the flow that mounts the panel.
     * The panel knows nothing about the item it is editing, so a caller that DOES —
     * "this dish already carries that product", "this swap changes the unit type" —
     * supplies the line rather than the panel growing domain knowledge.
     *
     * `placement` says where it is being asked for: `'list'` under each candidate in
     * the picker (seen BEFORE choosing), `'selected'` beside the quantity input (seen
     * while deciding the amount). Returning null for either is fine. Omitting the prop
     * entirely leaves every existing flow byte-identical.
     */
    candidateNote?: (candidate: SwapCandidate, placement: 'list' | 'selected') => React.ReactNode;
}

const MAX_SUGGESTIONS_PER_SOURCE = 5;
const ALL_FILTER = 'all';
// Rendering a whole catalogue at once is what made the list unusable, and this
// panel only shows ~4 rows at a time (max-h-64). Anything beyond the cap is
// reported as "+N more".
const MAX_VISIBLE_RESULTS = 30;

type SwapTab = 'suggested' | 'products' | 'subrecipes' | 'custom';

const SwapIngredientPanel: React.FC<SwapIngredientPanelProps> = ({
    ingredient,
    requireQuantityOnUnitChange = false,
    productMatching = false,
    ingredientIndex,
    itemId,
    dataOverride,
    defaultWastePercentage,
    adapter,
    onDone,
    candidateNote,
}) => {
    const { t } = useTranslation();
    const liveOrders = useOrderHistory(s => s.orderHistoryList);
    const orders = dataOverride?.orders ?? liveOrders;
    const liveCustoms = useCostPriceStore(s => s.customIngredients);
    const customIngredients = dataOverride?.customIngredients ?? liveCustoms;
    const liveSubrecipes = useSubrecipesStore(s => s.subrecipesList);
    const subrecipesList = useMemo(() => dataOverride ? Array.from(dataOverride.subrecipes.values()) : liveSubrecipes, [dataOverride, liveSubrecipes]);
    const liveSubrecipesById = useSubrecipesStore(s => s.subrecipesById);
    const subrecipesById = dataOverride?.subrecipes ?? liveSubrecipesById;
    const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);
    const lookupUsage = useProductUsage();

    const allowsSubrecipes = adapter.allowedSwapKinds.includes('subrecipe');
    const allowsCustom = adapter.allowedSwapKinds.includes('custom');

    // null = no explicit choice yet, so the panel picks the landing tab itself below.
    const [tabOverride, setTabOverride] = useState<SwapTab | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [supplierFilter, setSupplierFilter] = useState<string>(ALL_FILTER);
    const [categoryFilter, setCategoryFilter] = useState<string>(ALL_FILTER);
    const debouncedSearchQuery = useDebounce(searchQuery, 200);
    const [selected, setSelected] = useState<SwapCandidate | null>(null);
    const [quantityInput, setQuantityInput] = useState<string>("");
    const [isSwapping, setIsSwapping] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // After a successful swap: the same template ingredient on the other items,
    // offered for a one-click bulk apply. Null = nothing to offer / already handled.
    const [bulkApply, setBulkApply] = useState<{ candidate: SwapCandidate; matches: MatchingIngredientRef[] } | null>(null);

    // Unique products from the current restaurant's order history (latest occurrence
    // wins), keyed with the canonical aggregation key — exactly the AddIngredients
    // picker's dedup, so both surfaces show the same product universe.
    const uniqueProducts = useMemo<any[]>(() => Array.from(
        orders
            .flatMap(order => order.productData.map(product => ({
                ...product,
                platform: order.platform,
                orderDate: order.orderDate,
            })))
            .reduce((map, product) => {
                const agg = getProductAggregationKey(product, product.platform);
                if (!agg) return map;
                const existing = map.get(agg.key);
                if (!existing || new Date(product.orderDate) > new Date(existing.orderDate)) {
                    map.set(agg.key, { ...product, searchableId: agg.identifier });
                }
                return map;
            }, new Map<string, any>())
            .values()
    ), [orders]);

    // Supplier + product-category options, built from the products actually present.
    const supplierOptions = useMemo(() => Array.from(
        new Set(uniqueProducts.map(p => p.platform).filter(Boolean) as string[])
    ).sort((a, b) => a.localeCompare(b)), [uniqueProducts]);

    const categoryOptions = useMemo(() => Array.from(
        new Set(uniqueProducts.map(p => p.category).filter(Boolean) as string[])
    ).sort((a, b) => a.localeCompare(b)), [uniqueProducts]);

    // Filters narrow the searchable set itself, so a query searches within the
    // selected supplier/category rather than being re-filtered afterwards.
    const filteredProductSource = useMemo(() => uniqueProducts.filter(product =>
        (supplierFilter === ALL_FILTER || product.platform === supplierFilter) &&
        (categoryFilter === ALL_FILTER || product.category === categoryFilter)
    ), [uniqueProducts, supplierFilter, categoryFilter]);

    const productResults = useProductSearch({
        data: filteredProductSource,
        keys: ['name', 'searchableId'],
        query: debouncedSearchQuery,
        limit: MAX_VISIBLE_RESULTS,
    });

    const customResults = useProductSearch({
        data: customIngredients,
        keys: ['productName', 'articleId'],
        query: debouncedSearchQuery,
        limit: MAX_VISIBLE_RESULTS,
    });

    const subrecipeResults = useProductSearch({
        data: subrecipesList,
        keys: ['name', 'description'],
        query: debouncedSearchQuery,
        limit: MAX_VISIBLE_RESULTS,
    });

    const hasQuery = debouncedSearchQuery.trim().length > 0;
    // With a query the relevance ranking decides the order; without one the tab is a
    // browsable alphabetical list of its whole source. Both are capped at
    // MAX_VISIBLE_RESULTS and report the remainder, so a big catalogue never renders
    // in full and a search result is never silently truncated.
    const visibleProducts = useMemo(() => (hasQuery
        ? productResults.results
        : [...filteredProductSource]
            .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
            .slice(0, MAX_VISIBLE_RESULTS)
    ), [productResults, hasQuery, filteredProductSource]);
    const visibleCustom = useMemo(() => (hasQuery
        ? customResults.results
        : [...customIngredients]
            .sort((a, b) => a.productName.localeCompare(b.productName))
            .slice(0, MAX_VISIBLE_RESULTS)
    ), [customResults, hasQuery, customIngredients]);
    const visibleSubrecipes = useMemo(() => (hasQuery
        ? subrecipeResults.results
        : [...subrecipesList]
            .sort((a, b) => a.name.localeCompare(b.name))
            .slice(0, MAX_VISIBLE_RESULTS)
    ), [subrecipeResults, hasQuery, subrecipesList]);

    /**
     * The "Suggested" tab: the automation's own alternatives first (it already
     * ranked those against this ingredient), then a fuzzy pass over products,
     * custom ingredients and subrecipes on the TEMPLATE name — so a bad match
     * can be fixed without typing anything. Duplicates of an alternative are
     * dropped so the same product never shows twice.
     */
    const suggestedProducts = useProductSearch({
        data: uniqueProducts,
        keys: ['name', 'searchableId'],
        query: ingredient.templateName,
        limit: MAX_SUGGESTIONS_PER_SOURCE,
    });
    const suggestedCustom = useProductSearch({
        data: customIngredients,
        keys: ['productName'],
        query: ingredient.templateName,
        limit: MAX_SUGGESTIONS_PER_SOURCE,
    });
    const suggestedSubrecipes = useProductSearch({
        data: subrecipesList,
        keys: ['name', 'description'],
        query: ingredient.templateName,
        limit: MAX_SUGGESTIONS_PER_SOURCE,
    });

    /**
     * Prefill the quantity for the freshly picked candidate:
     *  1. the automation's own quantity, but only when it was expressed in the SAME
     *     unit as this candidate (swapping kg→piece must not carry "120" across),
     *  2. else the template's quantity converted into the candidate's unit,
     *  3. else a sensible per-unit default.
     */
    const defaultQuantityFor = (targetUnit: string | undefined): string => {
        const currentQty = ingredient.status === 'subrecipe'
            ? ingredient.subrecipePortionsNeeded
            : ingredient.quantityNeeded;
        const currentUnit = ingredient.status === 'subrecipe' ? undefined : ingredient.quantityInfo?.stdUnit;
        if (currentQty && currentQty > 0 && currentUnit && currentUnit === targetUnit) {
            return currentQty.toString();
        }
        if (requireQuantityOnUnitChange) return '';
        const converted = templateQuantityInUnit(ingredient.templateQuantity, ingredient.templateUnit, targetUnit);
        if (converted !== null) return converted.toString();
        return targetUnit === Unit.GRAM || targetUnit === Unit.MILLILITER ? "100" : "1";
    };

    /**
     * The recipe file's own amount expressed in the selected candidate's unit, or
     * null when the units measure different things ("2 pieces" -> a per-kg product).
     * Surfaced next to the input so a wrong prefill is visible instead of silent.
     */
    const recipeQuantityInSelectedUnit = (): number | null => {
        if (!selected) return null;
        return templateQuantityInUnit(
            ingredient.templateQuantity,
            ingredient.templateUnit,
            selected.quantityInfo?.stdUnit ?? selected.portionsUnit
        );
    };

    const select = (candidate: SwapCandidate) => {
        setSelected(candidate);
        setQuantityInput(defaultQuantityFor(candidate.quantityInfo?.stdUnit ?? candidate.portionsUnit));
        setError(null);
    };

    /** `€8.40/kg`-style label, the same per-unit price the AddIngredients picker shows. */
    const unitPriceLabel = (price?: number, quantityInfo?: any, category?: string): string | undefined => {
        if (price === undefined) return undefined;
        const unitPrice = calculateUnitPrice(price, quantityInfo, category);
        return unitPrice ? `${eur(unitPrice.value)}/${unitPrice.unit}` : undefined;
    };

    // Live cost of one portion unit of each subrecipe, computed once per data change
    // (the calc recurses into nested subrecipes, so it is not free per render).
    const subrecipeUnitCostById = useMemo(() => {
        const map = new Map<string, number>();
        subrecipesList.forEach(subrecipe => {
            map.set(subrecipe.id, calculateSubrecipeUnitCost(
                subrecipe,
                orders,
                customIngredients,
                showExVatOrders,
                subrecipesById,
                defaultWastePercentage
            ));
        });
        return map;
    }, [subrecipesList, orders, customIngredients, showExVatOrders, subrecipesById, defaultWastePercentage]);

    // An alternative can come from either pool — a custom one must stay 'custom'
    // so the swap writes a custom row instead of an order-history one.
    const alternativeCandidate = (alternative: ReviewAlternative, i: number): SwapCandidate => ({
        kind: alternative.isCustom ? 'custom' : 'product',
        key: `alt-${i}`,
        displayName: alternative.productName,
        articleId: alternative.articleId,
        platform: alternative.platform,
        customPlatformName: alternative.customPlatformName,
        quantityInfo: alternative.quantityInfo,
        price: alternative.latestPrice,
        unitPriceLabel: unitPriceLabel(alternative.latestPrice, alternative.quantityInfo),
    });

    const productCandidate = (product: any): SwapCandidate => ({
        kind: 'product',
        // searchableId is the resolved product identifier (falls back to the product
        // name on platforms without article ids) — raw articleId collapses every
        // no_article_id product onto one key, causing duplicate-key warnings and
        // selection/state reuse across all of a supplier's products.
        key: `product-${product.searchableId ?? product.articleId}-${product.platform}`,
        displayName: product.name,
        articleId: product.articleId,
        platform: product.platform,
        quantityInfo: product.quantityInfo || { stdAmount: 1, stdMultiplier: 1, stdUnit: 'p' },
        price: product.price,
        unitPriceLabel: unitPriceLabel(product.price, product.quantityInfo, product.category),
        categoryLabel: getCategoryDisplayName(product.category, product.mainCategory) ?? undefined,
    });

    const customCandidate = (ci: any): SwapCandidate => ({
        kind: 'custom',
        key: `custom-${ci.id}`,
        displayName: ci.productName,
        articleId: ci.articleId,
        platform: ci.platform,
        customPlatformName: ci.customPlatformName,
        quantityInfo: ci.quantityInfo,
        price: ci.price,
        unitPriceLabel: unitPriceLabel(ci.price, ci.quantityInfo),
    });

    const subrecipeCandidate = (subrecipe: any): SwapCandidate => {
        const unitCost = subrecipeUnitCostById.get(subrecipe.id) ?? 0;
        return {
            kind: 'subrecipe',
            key: `subrecipe-${subrecipe.id}`,
            displayName: subrecipe.name,
            subrecipeId: subrecipe.id,
            portionsUnit: subrecipe.portionsUnit,
            price: unitCost,
            unitPriceLabel: unitCost > 0 ? `${eur(unitCost)}/${subrecipe.portionsUnit || 'p'}` : undefined,
        };
    };

    // Suggested list: report alternatives first, then the best template-name matches
    // from each source. Cheap enough to build per render (the fuzzy passes memoize).
    const suggestedCandidates = (() => {
        const seen = new Set<string>();
        const out: SwapCandidate[] = [];
        const push = (candidate: SwapCandidate, dedupKey: string) => {
            if (seen.has(dedupKey)) return;
            seen.add(dedupKey);
            out.push(candidate);
        };
        // Dedup on the resolved identifier, not the raw articleId — on platforms
        // without article ids the raw value is the shared 'no_article_id' sentinel,
        // which would collapse all of a supplier's products into one suggestion.
        const productDedupKey = (platform?: string, articleId?: string, name?: string) => {
            const agg = getProductAggregationKey({ articleId, name }, (platform ?? '').toLowerCase());
            return `product-${(agg?.key ?? `${platform ?? ''}-${articleId || name || ''}`).toLowerCase()}`;
        };

        ingredient.alternatives.forEach((alternative, i) =>
            push(alternativeCandidate(alternative, i), productDedupKey(alternative.platform, alternative.articleId, alternative.productName)));
        suggestedProducts.results.forEach((product: any) =>
            push(productCandidate(product), productDedupKey(product.platform, product.articleId, product.name)));
        if (allowsSubrecipes) {
            suggestedSubrecipes.results.forEach((subrecipe: any) =>
                push(subrecipeCandidate(subrecipe), `subrecipe-${subrecipe.id}`));
        }
        if (allowsCustom) {
            suggestedCustom.results.forEach((custom: any) =>
                push(customCandidate(custom), `custom-${custom.id}`));
        }
        return out;
    })();

    // Landing on an empty "Suggested" is a wasted click, so when the template name
    // matched nothing the panel opens on the order-history browser instead. A tab
    // the user picked themselves always wins.
    const tab: SwapTab = tabOverride ?? (suggestedCandidates.length > 0 ? 'suggested' : 'products');

    const handleConfirmSwap = async () => {
        if (!selected) return;
        const quantity = parseFloat(quantityInput);
        if (!Number.isFinite(quantity) || quantity <= 0) return;

        setIsSwapping(true);
        setError(null);
        try {
            // Collect the sibling items BEFORE the swap — afterwards this item's
            // own entry is already on the new product and would drop out anyway,
            // but the others must still be read against the pre-swap report.
            const otherMatches = adapter.findSameIngredientElsewhere(
                ingredient.templateName,
                itemId,
                selected,
                quantity
            );

            await adapter.swap(itemId, ingredient, ingredientIndex, selected, quantity);

            if (otherMatches.length > 0) {
                setBulkApply({ candidate: selected, matches: otherMatches });
                return;
            }
            onDone();
        } catch (err) {
            console.error('Failed to swap ingredient:', err);
            setError(err instanceof StaleRecipeWriteError ? err.message : t('costPrice.automationReview.updateFailed'));
        } finally {
            setIsSwapping(false);
        }
    };

    const renderCandidateButton = (candidate: SwapCandidate) => {
        const isSelected = selected?.key === candidate.key;
        const platformLabel = candidate.customPlatformName || candidate.platform;
        // Only order-history products have purchase history to judge; custom
        // ingredients and subrecipes never appear on a supplier order.
        const usage = candidate.kind === 'product'
            ? lookupUsage(candidate.platform, candidate.articleId, candidate.displayName)
            : null;
        return (
            <button
                key={candidate.key}
                type="button"
                onClick={() => select(candidate)}
                className={`w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
                    isSelected
                        ? 'border-darkGreenColor dark:border-darkGreenColor-dark bg-[#F1FAF7] dark:bg-green-900/20'
                        : borderPrimary + ' bg-backgroundSecondary dark:bg-backgroundSecondary-dark hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark'
                }`}
            >
                <div className="min-w-0">
                    <p className={`text-[14px] leading-5 font-medium ${primaryText} truncate`}>{candidate.displayName}</p>
                    {candidate.quantityInfo && (
                        <p className={`text-[12px] ${greyTextColor}`}>
                            {candidate.quantityInfo.stdAmount} x {candidate.quantityInfo.stdMultiplier} {candidate.quantityInfo.stdUnit}
                            {candidate.price !== undefined ? ` · ${eur(candidate.price)}` : ''}
                            {candidate.categoryLabel ? ` · ${candidate.categoryLabel}` : ''}
                        </p>
                    )}
                    {candidate.kind === 'subrecipe' && (
                        <p className={`text-[12px] ${greyTextColor}`}>
                            {t('costPrice.automationReview.subrecipeBadge')}
                            {candidate.portionsUnit ? ` · ${candidate.portionsUnit}` : ''}
                        </p>
                    )}
                    {/* Per-unit price — for subrecipes this is the live cost of one
                        portion unit, which the list used to omit entirely. */}
                    {candidate.unitPriceLabel && (
                        <p className={`text-[12px] font-semibold ${primaryText}`}>{candidate.unitPriceLabel}</p>
                    )}
                    {candidateNote?.(candidate, 'list')}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {candidate.kind === 'product' && <UsageBadge usage={usage} compact />}
                    {candidate.kind === 'product' && isKnownPlatform(candidate.platform) ? (
                        <SupplierLogo
                            platform={candidate.platform!.toLowerCase() as Platform}
                            size="small"
                            alt={candidate.platform}
                            className="h-5 max-w-[70px] object-contain flex-shrink-0"
                        />
                    ) : platformLabel ? (
                        <span className={`text-[12px] ${greyTextColor} flex-shrink-0`}>{platformLabel}</span>
                    ) : null}
                </div>
            </button>
        );
    };

    const selectedUnit = selected?.quantityInfo?.stdUnit ?? selected?.portionsUnit ?? '';

    // The swap itself is done — the panel now only asks whether to push the same
    // correction to the other dishes carrying this template ingredient.
    if (bulkApply) {
        return (
            <ApplyToOtherItemsPanel
                productMatching={productMatching}
                templateName={ingredient.templateName}
                candidate={bulkApply.candidate}
                matches={bulkApply.matches}
                adapter={adapter}
                onDone={onDone}
            />
        );
    }

    // Per-tab list. "Suggested" needs no search box — it is driven by the template
    // name; the other three are plain browse/search lists over one source each.
    const tabCandidates: SwapCandidate[] = tab === 'suggested'
        ? suggestedCandidates
        : tab === 'products'
            ? visibleProducts.map(productCandidate)
            : tab === 'subrecipes'
                ? visibleSubrecipes.map(subrecipeCandidate)
                : visibleCustom.map(customCandidate);

    // How many the tab's source holds in total, so a capped list can say so instead
    // of quietly pretending the catalogue ends at row 200.
    const tabTotal = tab === 'products'
        ? (hasQuery ? productResults.totalMatches : filteredProductSource.length)
        : tab === 'subrecipes'
            ? (hasQuery ? subrecipeResults.totalMatches : subrecipesList.length)
            : tab === 'custom'
                ? (hasQuery ? customResults.totalMatches : customIngredients.length)
                : tabCandidates.length;
    const hiddenCount = Math.max(0, tabTotal - tabCandidates.length);

    return (
        <div className={`mt-2 rounded-lg border ${borderPrimary} bg-background dark:bg-background-dark p-3 space-y-3`}>
            <TabbedSections
                options={[
                    { value: 'suggested', label: t('costPrice.automationReview.tabSuggested') },
                    { value: 'products', label: t('costPrice.addIngredient.fromOrders') },
                    // A flow that forbids nesting (the subrecipe import) never offers
                    // subrecipes as a swap target, so the tab is dropped entirely.
                    ...(allowsSubrecipes ? [{ value: 'subrecipes', label: t('costPrice.addIngredient.subrecipes') }] : []),
                    ...(allowsCustom ? [{ value: 'custom', label: t('costPrice.addIngredient.customIngredients') }] : []),
                ]}
                value={tab}
                onChange={(v) => setTabOverride(v as SwapTab)}
                isTimeOptionSelection={false}
                maxTabsBeforeDropdown={4}
                fullWidth
            />

            {tab !== 'suggested' && (
                <input
                    type="text"
                    placeholder={t('costPrice.automationReview.searchProducts')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`w-full rounded-lg border bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-3 h-9 text-[14px] ${secondaryText} ${placeholderPrimary} outline-none border-borderGrey dark:border-borderGrey-dark`}
                />
            )}

            {/* Supplier + product category narrow the order-history universe before
                the search runs — the full list is thousands of products. */}
            {tab === 'products' && (
                <div className="flex gap-2">
                    <select
                        value={supplierFilter}
                        onChange={(e) => setSupplierFilter(e.target.value)}
                        className={`flex-1 min-w-0 rounded-lg border bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-2 h-9 text-[13px] ${secondaryText} outline-none border-borderGrey dark:border-borderGrey-dark`}
                    >
                        <option value={ALL_FILTER}>{t('costPrice.automationReview.allSuppliers')}</option>
                        {supplierOptions.map(platform => (
                            <option key={platform} value={platform}>{platform}</option>
                        ))}
                    </select>
                    <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className={`flex-1 min-w-0 rounded-lg border bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-2 h-9 text-[13px] ${secondaryText} outline-none border-borderGrey dark:border-borderGrey-dark`}
                    >
                        <option value={ALL_FILTER}>{t('costPrice.automationReview.allCategories')}</option>
                        {categoryOptions.map(category => (
                            <option key={category} value={category}>{getCategoryDisplayName(category) ?? category}</option>
                        ))}
                    </select>
                </div>
            )}

            <div className="max-h-64 overflow-y-auto space-y-1.5">
                {tabCandidates.length === 0 ? (
                    <p className={`text-[12px] ${greyTextColor} px-1 py-2`}>
                        {tab === 'suggested'
                            ? t('costPrice.automationReview.noSuggestions')
                            : t('costPrice.automationReview.noResults')}
                    </p>
                ) : (
                    tabCandidates.map(renderCandidateButton)
                )}
            </div>
            {tab !== 'suggested' && tabCandidates.length > 0 && (
                <p className={`text-[11px] ${greyTextColor}`}>
                    {hiddenCount > 0
                        ? t('costPrice.addIngredient.showingFirstResults', { count: tabCandidates.length, more: hiddenCount })
                        : t('costPrice.automationReview.resultCount', { count: tabCandidates.length })}
                </p>
            )}

            {/* Quantity + confirm */}
            {selected && (
                <div className="space-y-1.5">
                    <div className="flex items-end gap-3">
                        <div className="flex-1">
                            <label className={`block text-[12px] font-semibold ${secondaryText} mb-1`}>
                                {t('costPrice.automationReview.quantityLabel', { unit: selectedUnit })}
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="any"
                                value={quantityInput}
                                onChange={(e) => setQuantityInput(e.target.value)}
                                className={`w-full rounded-lg border bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-3 h-9 text-[14px] ${secondaryText} outline-none border-borderGrey dark:border-borderGrey-dark`}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={handleConfirmSwap}
                            disabled={isSwapping || !(parseFloat(quantityInput) > 0)}
                            className={`h-9 px-4 justify-center text-[14px] font-semibold ${greenColorClass} disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                        >
                            {isSwapping && <Loader2 className="h-4 w-4 animate-spin" />}
                            {isSwapping ? t('costPrice.automationReview.swapping') : t('costPrice.automationReview.confirmSwap')}
                        </button>
                    </div>
                    {/* What the recipe file itself asked for, always visible. When the
                        units can be converted it is one click away; when they can't
                        (pieces -> per-kg) the prefill is a guess and says so. */}
                    <div className={`flex items-center gap-2 text-[12px] ${greyTextColor}`}>
                        <span>
                            {t('costPrice.automationReview.recipeAmount', {
                                quantity: ingredient.templateQuantity,
                                unit: ingredient.templateUnit,
                            })}
                        </span>
                        {(() => {
                            const recipeQuantity = recipeQuantityInSelectedUnit();
                            if (recipeQuantity === null) {
                                return <span className="italic">{t('costPrice.automationReview.recipeAmountNotConvertible')}</span>;
                            }
                            if (recipeQuantity.toString() === quantityInput) return null;
                            return (
                                <button
                                    type="button"
                                    onClick={() => setQuantityInput(recipeQuantity.toString())}
                                    className="font-semibold text-darkGreenColor dark:text-darkGreenColor-dark hover:underline"
                                >
                                    {t('costPrice.automationReview.useRecipeAmount', { quantity: recipeQuantity, unit: selectedUnit })}
                                </button>
                            );
                        })()}
                    </div>
                    {candidateNote?.(selected, 'selected')}
                </div>
            )}

            {error && (
                <p className="text-[13px] text-red-600 dark:text-red-400">{error}</p>
            )}

            <button
                type="button"
                onClick={onDone}
                className={`text-[13px] font-medium ${greyTextColor} hover:opacity-70`}
            >
                {t('costPrice.automationReview.cancel')}
            </button>
        </div>
    );
};

export default SwapIngredientPanel;
